import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { colors, spacing, radius, shadow, zIndex, typography, transitions } from '../../theme/tokens';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Modal — primitive con portal, focus trap, Esc y click-out opcional.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {React.ReactNode} title — header
 * @param {React.ReactNode} footer — botones al pie
 * @param {React.ReactNode} children — body
 * @param {boolean} closeOnBackdrop — default true
 * @param {boolean} closeOnEsc — default true
 * @param {'sm'|'md'|'lg'} size — default md
 * @param {string} ariaLabel — si no hay title, aria-label requerido para a11y
 * @param {'modal'|'sessionBlock'} level — capa de apilamiento. `sessionBlock` va por encima de
 *        todo, incluidos los overlays con z-index literal que no usan los tokens (#171).
 */
const Modal = ({
  open,
  onClose,
  title,
  footer,
  children,
  closeOnBackdrop = true,
  closeOnEsc = true,
  size = 'md',
  ariaLabel,
  level = 'modal',
}) => {
  const dialogRef = useRef(null);
  const previousActiveRef = useRef(null);

  const handleKey = useCallback((e) => {
    if (!open) return;
    if (e.key === 'Escape') {
      // Se corta siempre, cierre o no. Con closeOnEsc en false esta rama ni se entraba, asi que
      // el Escape seguia viaje hasta un modal que hubiera quedado abierto detras y lo cerraba —
      // interactuando con la app de fondo desde un dialogo que dice bloquearla (#171).
      e.stopPropagation();
      if (closeOnEsc) onClose?.();
      return;
    }
    if (e.key === 'Tab' && dialogRef.current) {
      const focusables = dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      // Si el foco quedo FUERA del dialogo, el trap no matcheaba ninguna rama y el Tab se iba a
      // la app de atras. Pasa con solo hacer click en el backdrop: eso desenfoca y deja
      // activeElement en body (#171).
      if (!dialogRef.current.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [open, closeOnEsc, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    previousActiveRef.current = document.activeElement;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    // Foco inicial: primer focusable o el dialog mismo
    requestAnimationFrame(() => {
      const node = dialogRef.current;
      if (!node) return;
      const first = node.querySelector(FOCUSABLE_SELECTOR);
      (first || node).focus();
    });

    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      body.style.overflow = prevOverflow;
      const prev = previousActiveRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open, handleKey]);

  if (!open) return null;

  const sizeWidth = { sm: 360, md: 480, lg: 640 }[size] || 480;

  const content = (
    <div
      role="presentation"
      onClick={closeOnBackdrop ? onClose : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(17, 24, 39, 0.5)',
        zIndex: zIndex[level] ?? zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing[4],
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={!title ? ariaLabel : undefined}
        aria-labelledby={title ? 'boveda-modal-title' : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.surface,
          borderRadius: radius.md,
          boxShadow: shadow.lg,
          width: '100%',
          maxWidth: sizeWidth,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: `boveda-modal-in ${transitions.base} both`,
        }}
      >
        {title && (
          <div
            style={{
              padding: `${spacing[4]}px ${spacing[5]}px`,
              borderBottom: `1px solid ${colors.border}`,
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text,
            }}
            id="boveda-modal-title"
          >
            {title}
          </div>
        )}
        <div
          style={{
            padding: `${spacing[4]}px ${spacing[5]}px`,
            overflowY: 'auto',
            flex: 1,
            fontSize: typography.fontSize.base,
            color: colors.text,
            lineHeight: typography.lineHeight.normal,
          }}
        >
          {children}
        </div>
        {footer && (
          <div
            style={{
              padding: `${spacing[3]}px ${spacing[5]}px`,
              borderTop: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: spacing[2],
              backgroundColor: colors.gray50,
            }}
          >
            {footer}
          </div>
        )}
      </div>
      <style>{`
        @keyframes boveda-modal-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );

  return createPortal(content, document.body);
};

export default Modal;
